import type { StatusCode } from 'hono/utils/http-status'

type HttpErrorOptions<Cause = unknown> = {
  code: string
  message: string
  reason?: unknown
  cause?: Cause
  /** Normalized server transport metadata; never raw response headers or credentials. */
  authenticationFailure?: 'BEARER_INVALID_TOKEN'
}

export type PlainHttpError<Cause = unknown> = {
  readonly status: StatusCode
  readonly cause: Cause
} & HttpErrorOptions

export class HttpError<Cause = unknown> extends Error {
  public readonly status: StatusCode
  public readonly code: string
  public readonly message: string
  public readonly reason?: unknown
  public readonly cause?: Cause
  public readonly authenticationFailure?: 'BEARER_INVALID_TOKEN'

  static fromResponse<E extends HttpErrorOptions>(status: number, error: E) {
    return new HttpError(status as StatusCode, error)
  }

  constructor(status: StatusCode, options: HttpErrorOptions<Cause>) {
    super(`${options.code}: ${options.message}`)
    Object.setPrototypeOf(this, HttpError.prototype)

    this.name = HttpError.prototype.constructor.name

    this.status = status
    this.code = options.code
    this.message = options.message
    this.reason = options.reason
    this.cause = options.cause
    this.authenticationFailure = options.authenticationFailure
    if (options.cause instanceof Error && options.cause.stack) {
      this.stack = options.cause.stack
    }
  }

  getResponseBody(options?: { debug: boolean }) {
    return {
      ...(options?.debug && { cause: this.cause }),
      error: {
        code: this.code,
        message: this.message,
        reason: this.reason,
      },
    }
  }
  toPlain<Cause = unknown>(): PlainHttpError<Cause> {
    return {
      status: this.status,
      code: this.code,
      message: this.message,
      reason: this.reason,
      cause: this.cause as Cause,
    }
  }
}
